import { useEffect, useRef, useState } from "react";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Падающие иконки на стартовом экране. Создаются напрямую в DOM: без перерисовок React. */
export function FallingIcons() {
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || reducedMotion()) return;
    const icons = ["🎬", "🍿", "📽️", "🎥", "⭐"];
    const cleanups = new Set<ReturnType<typeof setTimeout>>();

    const spawn = () => {
      const icon = document.createElement("div");
      icon.textContent = icons[Math.floor(Math.random() * icons.length)] ?? "🎬";
      Object.assign(icon.style, {
        position: "absolute",
        fontSize: `${Math.random() * 30 + 20}px`,
        left: `${Math.random() * Math.max(0, layer.clientWidth - 50)}px`,
        top: "-50px",
        opacity: String(Math.random() * 0.8 + 0.2),
        pointerEvents: "none",
        transition: `transform ${Math.random() * 3 + 3}s linear, opacity 0.5s`,
      });
      layer.appendChild(icon);
      requestAnimationFrame(() => {
        icon.style.transform = `translateY(${layer.clientHeight + 50}px) rotate(${Math.random() * 360}deg)`;
      });
      const timer = setTimeout(() => {
        icon.remove();
        cleanups.delete(timer);
      }, 5000);
      cleanups.add(timer);
    };

    const interval = setInterval(spawn, 300);
    return () => {
      clearInterval(interval);
      cleanups.forEach(clearTimeout);
      layer.replaceChildren();
    };
  }, []);

  return <div ref={layerRef} className="falling-layer" aria-hidden="true" />;
}

const RAIN = ["🎬", "🎥", "🍿", "✨", "⭐", "🌟"];

/** Эмодзи-дождь при выборе фильма: 35 штук потоком, через 5 секунд убирается. */
export function EmojiRain() {
  const [items] = useState(() =>
    Array.from({ length: 35 }, (_, i) => ({
      emoji: RAIN[Math.floor(Math.random() * RAIN.length)] ?? "🎬",
      left: Math.random() * 100,
      size: 24 + Math.random() * 20,
      duration: 0.8 + Math.random() * 0.6,
      delay: i * 0.04,
    })),
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;
  return (
    <div aria-hidden="true">
      {items.map((item, i) => (
        <div
          key={i}
          className="emoji-rain"
          style={{
            left: `${item.left}vw`,
            fontSize: `${item.size}px`,
            animationDuration: `${item.duration}s`,
            animationDelay: `${item.delay}s`,
          }}
        >
          {item.emoji}
        </div>
      ))}
    </div>
  );
}

const QUOTES = [
  "Это я удачно зашел.",
  "Держи своих друзей близко. А врагов еще ближе.",
  "Маленькая ложь рождает большое недоверие.",
  "Любовь не задает вопросов.",
  "Страх делает тебя пленником. Надежда дает тебе свободу.",
  "Нет ничего лучше, чем быть дома.",
  "Нельзя просто так взять и...",
  "Глупостей не надо делать даже со скуки.",
  "I'll be back.",
];

/** «Печатающиеся» цитаты на стартовом экране: набор → пауза → стирание → следующая. */
export function Typewriter({ quotes = QUOTES }: { quotes?: readonly string[] }) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (quotes.length === 0) return;
    let timer: ReturnType<typeof setTimeout>;
    let quoteIndex = 0;
    let length = 0;
    let erasing = false;

    const tick = () => {
      const quote = quotes[quoteIndex] ?? "";
      if (!erasing) {
        length += 1;
        setText(quote.slice(0, length));
        if (length >= quote.length) {
          erasing = true;
          timer = setTimeout(tick, 1500);
          return;
        }
        timer = setTimeout(tick, 50);
      } else {
        length -= 1;
        setText(quote.slice(0, Math.max(length, 0)));
        if (length <= 0) {
          erasing = false;
          quoteIndex = (quoteIndex + 1) % quotes.length;
          timer = setTimeout(tick, 50);
          return;
        }
        timer = setTimeout(tick, Math.max(8, 300 / quote.length));
      }
    };
    timer = setTimeout(tick, 50);
    return () => clearTimeout(timer);
  }, [quotes]);

  return (
    <div className="quotes" aria-hidden="true">
      {text}
    </div>
  );
}
